/**
 * SSOT — מדיניות חיוב מנוי HotelsRiver + Morning recurring.
 * יום חיוב קבוע: 1 בחודש. תדירות: חודשי.
 */

export const SUBSCRIPTION_BILLING_DAY_OF_MONTH = 1;
export const SUBSCRIPTION_BILLING_FREQUENCY = "monthly";
export const MORNING_RECURRING_BILLING_DAY = 1;
export const SUBSCRIPTION_BILLING_TIMEZONE = "Asia/Jerusalem";

const STANDING_ORDER_LABELS_HE = {
  not_configured: "לא הוגדרה",
  pending: "ממתינה לאישור",
  active: "פעילה",
};

/**
 * @param {unknown} status
 * @returns {string}
 */
export function standingOrderStatusLabelHe(status) {
  const key = String(status || "").trim().toLowerCase();
  return STANDING_ORDER_LABELS_HE[key] || STANDING_ORDER_LABELS_HE.not_configured;
}

/**
 * @param {unknown} isoOrDisplay — ISO מהשרת או DD/MM/YYYY
 * @returns {string|null}
 */
export function formatNextBillingDateDisplay(isoOrDisplay) {
  if (!isoOrDisplay) return null;
  const raw = String(isoOrDisplay).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  try {
    return new Intl.DateTimeFormat("he-IL", {
      timeZone: SUBSCRIPTION_BILLING_TIMEZONE,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(d);
  } catch {
    return raw;
  }
}

/**
 * @param {{
 *   packageCode?: string|null,
 *   monthlyGrossAmount?: number|null,
 *   nextBillingDateDisplay?: string|null,
 *   nextChargeAt?: string|null,
 *   standingOrderStatus?: string|null,
 *   standingOrderStatusHe?: string|null,
 *   subscriptionBilling?: object|null,
 *   displayBilling?: object|null,
 * }} sources
 */
export function resolveSubscriptionBillingSummary(sources = {}) {
  const policy =
    sources.subscriptionBilling ||
    sources.displayBilling?.subscription_billing ||
    null;

  const packageCode =
    policy?.packageCode ||
    sources.packageCode ||
    sources.displayBilling?.package_code ||
    null;

  const monthlyGrossAmount =
    policy?.monthlyGrossAmount ??
    sources.monthlyGrossAmount ??
    sources.displayBilling?.monthly_gross_amount ??
    null;

  const nextBillingDateDisplay =
    policy?.nextBillingDateDisplay ||
    sources.nextBillingDateDisplay ||
    sources.displayBilling?.next_billing_date_display ||
    formatNextBillingDateDisplay(sources.nextChargeAt);

  const standingOrderStatus =
    policy?.standingOrderStatus ||
    sources.standingOrderStatus ||
    sources.displayBilling?.standing_order_status ||
    "not_configured";

  const standingOrderStatusHe =
    policy?.standingOrderStatusHe ||
    sources.standingOrderStatusHe ||
    sources.displayBilling?.standing_order_status_he ||
    standingOrderStatusLabelHe(standingOrderStatus);

  return {
    packageCode: packageCode ? String(packageCode).trim().toUpperCase() : null,
    monthlyGrossAmount:
      monthlyGrossAmount != null && Number.isFinite(Number(monthlyGrossAmount))
        ? Number(monthlyGrossAmount)
        : null,
    nextBillingDateDisplay: nextBillingDateDisplay || null,
    standingOrderStatus,
    standingOrderStatusHe,
    billingDayOfMonth: SUBSCRIPTION_BILLING_DAY_OF_MONTH,
    billingFrequency: SUBSCRIPTION_BILLING_FREQUENCY,
  };
}

/**
 * @param {number|null} grossAmount
 */
export function formatMonthlyGrossAmountIls(grossAmount) {
  if (grossAmount == null || !Number.isFinite(Number(grossAmount))) return "—";
  const n = Number(grossAmount);
  const hasFraction = Math.abs(n % 1) > 1e-9;
  try {
    const formatted = new Intl.NumberFormat("he-IL", {
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0,
    }).format(n);
    return `${formatted} ₪`;
  } catch {
    return `${n} ₪`;
  }
}
